import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Spin, Button, Empty, Progress, Typography, message } from 'antd';
import { DesktopOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { HomeContainer, PageHeader, PageTitle, PageSubtitle } from '../Home/styles';
import { Card as StyledCard } from '../PairTrading/styles';
import {
    DesktopOs,
    DesktopRelease,
    DesktopReleaseAsset,
    detectOs,
    download,
    getLatest,
    osName,
    pickPrimary,
    platformLabel,
} from '../../services/desktopReleaseApi';
import { formatDecimal } from '../../utils/numberFormat';

const { Content } = Layout;
const { Text, Paragraph } = Typography;

const sizeMb = (bytes: number) => `${formatDecimal(bytes / (1024 * 1024), 1)} MB`;

// Installs the newest downloaded .dmg into Applications and clears the quarantine flag: an
// un-notarized build is otherwise reported "damaged" with no Open Anyway.
export const MAC_INSTALL_COMMAND = [
    'DMG=$(ls -t ~/Downloads/Fortuna\\ Desktop_*.dmg | head -1) && \\',
    "MNT=$(hdiutil attach -nobrowse -readonly \"$DMG\" | awk -F'\\t' '/\\/Volumes\\//{print $NF}') && \\",
    'rm -rf "/Applications/Fortuna Desktop.app" && cp -R "$MNT/Fortuna Desktop.app" /Applications/ && \\',
    'hdiutil detach -quiet "$MNT" && \\',
    'xattr -dr com.apple.quarantine "/Applications/Fortuna Desktop.app" && \\',
    'open "/Applications/Fortuna Desktop.app"',
].join('\n');

export const MAC_UNBLOCK_COMMAND = 'xattr -dr com.apple.quarantine "/Applications/Fortuna Desktop.app" && open "/Applications/Fortuna Desktop.app"';

const FIRST_OPEN: Record<DesktopOs, { title: string; steps: string[]; commands?: { label: string; text: string }[] }> = {
    macos: {
        title: 'macOS',
        steps: [
            'Download the .dmg and leave it in your Downloads folder.',
            'Quit Fortuna Desktop if it is running.',
            'Open Terminal (⌘ Space, type Terminal), paste the command below and press Return. It installs the app into Applications, clears the macOS download block and opens it. Run it again after each new download.',
        ],
        commands: [
            { label: 'Install and open', text: MAC_INSTALL_COMMAND },
            { label: 'Already in Applications and macOS says it "is damaged and can\'t be opened"', text: MAC_UNBLOCK_COMMAND },
        ],
    },
    windows: {
        title: 'Windows',
        steps: ["Run the installer; on 'Windows protected your PC' click More info → Run anyway."],
    },
};

const AssetMeta: React.FC<{ asset: DesktopReleaseAsset }> = ({ asset }) => (
    <div style={{ fontSize: 12, color: '#848E9C' }}>
        <div>
            {asset.file} · {sizeMb(asset.size)}
        </div>
        <div>
            SHA-256:{' '}
            <Text copyable={{ text: asset.sha256 }} style={{ fontSize: 12, color: '#848E9C', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {asset.sha256}
            </Text>
        </div>
    </div>
);

/** `embedded` drops the page frame and title, for the Settings tab. */
const DesktopDownload: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
    const [release, setRelease] = useState<DesktopRelease | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [arch, setArch] = useState<'arm' | 'x86' | undefined>(undefined);
    const os = useMemo(() => detectOs(), []);

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(false);
        try {
            setRelease(await getLatest());
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    // Chromium only; elsewhere the arm64-first preference stands.
    useEffect(() => {
        const uad = (navigator as any).userAgentData;
        uad?.getHighEntropyValues?.(['architecture'])
            .then((v: { architecture?: string }) => setArch(v.architecture === 'arm' ? 'arm' : v.architecture === 'x86' ? 'x86' : undefined))
            .catch(() => undefined);
    }, []);

    const keys = useMemo(() => Object.keys(release?.platforms ?? {}), [release]);
    const primary = useMemo(() => pickPrimary(keys, os, arch), [keys, os, arch]);
    const others = keys.filter((k) => k !== primary);

    const start = async (key: string) => {
        if (!release) return;
        setBusy(key);
        setProgress(null);
        try {
            await download(key, release.platforms[key].file, setProgress);
        } catch {
            message.error('Download failed. Please try again.');
        } finally {
            setBusy(null);
            setProgress(null);
        }
    };

    const steps = os ? [FIRST_OPEN[os]] : [FIRST_OPEN.macos, FIRST_OPEN.windows];

    const body = (
        <>
            {loading ? (
                <div style={{ textAlign: 'center', padding: 48 }}>
                    <Spin />
                </div>
            ) : failed ? (
                <StyledCard>
                    <Empty description="Could not load the desktop release.">
                        <Button icon={<ReloadOutlined />} onClick={() => void load()}>
                            Retry
                        </Button>
                    </Empty>
                </StyledCard>
            ) : !release ? (
                <StyledCard>
                    <Empty description="No desktop release is published yet." />
                </StyledCard>
            ) : (
                <>
                    <StyledCard style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ color: '#EAECEF' }}>
                                Version <strong>{release.version}</strong>
                                {release.publishedAt && (
                                    <span style={{ color: '#848E9C' }}>
                                        {' '}
                                        · published {dayjs(release.publishedAt).format('YYYY-MM-DD HH:mm')}
                                    </span>
                                )}
                            </div>
                            {primary && (
                                <div>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<DownloadOutlined />}
                                        loading={busy === primary}
                                        disabled={busy !== null}
                                        onClick={() => void start(primary)}
                                    >
                                        Download for {osName(primary)}
                                    </Button>
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ fontSize: 12, color: '#EAECEF' }}>{platformLabel(primary)}</div>
                                        <AssetMeta asset={release.platforms[primary]} />
                                    </div>
                                </div>
                            )}
                            {busy && progress !== null && <Progress percent={Math.round(progress * 100)} size="small" />}
                            {release.notes && (
                                <Paragraph style={{ whiteSpace: 'pre-wrap', color: '#B7BDC6', marginBottom: 0 }}>{release.notes}</Paragraph>
                            )}
                        </div>
                    </StyledCard>

                    {others.length > 0 && (
                        <StyledCard title={primary ? 'Other platforms' : 'Downloads'} style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {others.map((k) => (
                                    <div key={k} data-testid={`platform-${k}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ color: '#EAECEF' }}>{platformLabel(k)}</div>
                                            <AssetMeta asset={release.platforms[k]} />
                                        </div>
                                        <Button
                                            icon={<DownloadOutlined />}
                                            loading={busy === k}
                                            disabled={busy !== null}
                                            onClick={() => void start(k)}
                                            aria-label={`Download ${platformLabel(k)}`}
                                        >
                                            Download
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </StyledCard>
                    )}

                    <StyledCard title="First open">
                        <div style={{ color: '#B7BDC6', fontSize: 13 }}>
                            <Paragraph style={{ color: '#848E9C', fontSize: 12 }}>
                                This build is not signed or notarized yet, so your system blocks the first launch.
                            </Paragraph>
                            {steps.map((s) => (
                                <div key={s.title} style={{ marginBottom: 8 }}>
                                    <div style={{ color: '#EAECEF', fontWeight: 600 }}>{s.title}</div>
                                    <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                                        {s.steps.map((t) => (
                                            <li key={t}>{t}</li>
                                        ))}
                                    </ol>
                                    {s.commands?.map((c) => (
                                        <div key={c.label} style={{ marginTop: 8 }}>
                                            <div style={{ fontSize: 12, color: '#848E9C', marginBottom: 4 }}>{c.label}</div>
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#0B0E11', border: '1px solid #2B3139', borderRadius: 6, padding: '8px 10px' }}>
                                                <pre data-testid="mac-command" style={{ margin: 0, flex: 1, minWidth: 0, overflowX: 'auto', fontSize: 12, color: '#EAECEF', fontFamily: 'monospace', whiteSpace: 'pre' }}>
                                                    {c.text}
                                                </pre>
                                                <Text copyable={{ text: c.text, tooltips: ['Copy', 'Copied'] }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                            <div>Then sign in with your Fortuna account; the server address is filled in for you.</div>
                        </div>
                    </StyledCard>
                </>
            )}
        </>
    );

    if (embedded) return body;

    return (
        <HomeContainer>
            <Content style={{ padding: '16px', maxWidth: 880 }}>
                <PageHeader>
                    <PageTitle>
                        <DesktopOutlined style={{ marginRight: '8px', color: '#0ECB81' }} />
                        Fortuna Desktop
                    </PageTitle>
                    <PageSubtitle>Places your CLIENT accounts' orders from your own machine.</PageSubtitle>
                </PageHeader>

                {body}
            </Content>
        </HomeContainer>
    );
};

export default DesktopDownload;
